import { HEADER_BYTES, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `target` frame (ADR-074 decision 9, PROTOCOL.md 5.8): 5 bytes, client to server, the entity a player selects,
 * `NO_HANDLE` to clear. Declared in protocol 5 and handled from 6.4.0 (P2.2-T3, ADR-034's amendment of 2026-10-01);
 * a map server before it drops the frame on its not-ready window. The decoder never throws (ADR-030).
 */

/**
 * Total frame length.
 */
export const TARGET_BYTES = 5;

/**
 * A decoded or to-be-encoded `target`, reused across frames.
 */
export interface TargetRecord {
  handle: number;
}

/**
 * A fresh record for `decodeTarget` to fill.
 *
 * @returns A zeroed record.
 */
export function newTargetRecord(): TargetRecord {
  return { handle: 0 };
}

/**
 * Encodes a `target` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `TARGET_BYTES` long.
 * @param target The handle.
 * @returns Bytes written, always `TARGET_BYTES`.
 * @throws Error when the buffer is too small or the handle is not a `u16`.
 */
export function encodeTarget(buffer: Uint8Array, target: Readonly<TargetRecord>): number {
  if (buffer.length < TARGET_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${TARGET_BYTES}-byte target`);
  }
  assertField(target.handle, MAX_U16, `target handle ${target.handle}`);
  putHeader(buffer, MessageType.Target);
  putU16(buffer, HEADER_BYTES, target.handle);
  return TARGET_BYTES;
}

/**
 * Decodes a `target` frame into a record. Never throws: a wrong length or type is malformed and a wrong version a
 * mismatch; every handle is well formed.
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeTarget(frame: Uint8Array, out: TargetRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.Target, TARGET_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  out.handle = readU16(frame, HEADER_BYTES);
  return OK;
}
